import { format } from 'date-fns';
import React from 'react';

import Loader from '../../../../components/Loader';
import useGamification from '../useGamification';

interface ProgressColorProps {
  percentage: string;
}

interface StatusProps {
  isTargetMet: boolean;
}

const Goals: React.FC = () => {
  const {
    availableWeeks,
    selectedWeek,
    weeklyPerformance,
    myWeeklyPerformance,
    todayTarget,
    loading,
    error,
    targetModalOpen,
    selectedUser,
    dailyTarget,
    targetNotes,
    targetDate,
    isAdmin,
    handleWeekChange,
    openTargetModal,
    closeTargetModal,
    handleSetTarget,
    setDailyTarget,
    setTargetNotes,
    setTargetDate,
    fetchTodayTarget,
    clearError,
  } = useGamification();

  const getProgressColor = ({ percentage }: ProgressColorProps): string => {
    const num = parseFloat(percentage);
    if (num >= 40) return 'bg-green-300';
    if (num >= 30) return 'bg-blue-300';
    if (num >= 20) return 'bg-yellow-300';
    return 'bg-red-300';
  };

  const getStatusColor = ({ isTargetMet }: StatusProps): string => {
    return isTargetMet ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = ({ isTargetMet }: StatusProps): string => {
    return isTargetMet ? '✓' : '✗';
  };

  const handleRefresh = (): void => {
    clearError();
    fetchTodayTarget();
    if (selectedWeek) {
      // This will trigger the useEffect in the hook to refetch weekly performance
      handleWeekChange(selectedWeek);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="p-6">
      {/* Header with Refresh Button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Goals & Performance</h1>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={clearError}
                  className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Target Section */}
      {todayTarget && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Today's Target</h2>
          {todayTarget.hasTarget ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {todayTarget.target?.dailyTarget || 0}
                  </div>
                  <div className="text-sm text-gray-600">Daily Target</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {todayTarget.target?.achievedLeads || 0}
                  </div>
                  <div className="text-sm text-gray-600">Achieved</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {todayTarget.progress.remaining}
                  </div>
                  <div className="text-sm text-gray-600">Remaining</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progress</span>
                  <span>{todayTarget.progress.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${getProgressColor({ percentage: todayTarget.progress.percentage })}`}
                    style={{ width: `${Math.min(parseFloat(todayTarget.progress.percentage), 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-semibold text-gray-700">Valid Leads</div>
                  <div className="text-lg font-bold text-green-600">{todayTarget.todayStats.validLeads}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-semibold text-gray-700">Total Leads</div>
                  <div className="text-lg font-bold text-blue-600">{todayTarget.todayStats.totalLeads}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-semibold text-gray-700">Excluded</div>
                  <div className="text-lg font-bold text-red-600">{todayTarget.todayStats.excludedLeads}</div>
                </div>
              </div>

              {todayTarget.target?.notes && (
                <div className="mt-4 p-3 bg-blue-50 rounded">
                  <div className="text-sm font-semibold text-blue-800">Notes:</div>
                  <div className="text-sm text-blue-700">{todayTarget.target.notes}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <div className="text-yellow-800 font-semibold">No target set for today</div>
              <div className="text-yellow-600 text-sm mt-1">Contact your admin to set a daily target</div>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Performance Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Leads This Week</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {myWeeklyPerformance?.totalLeads || weeklyPerformance?.data?.reduce((sum, user) => sum + user.totalLeads, 0) || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Valid Leads This Week</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {myWeeklyPerformance?.validLeads || weeklyPerformance?.data?.reduce((sum, user) => sum + user.validLeads, 0) || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Average Completion Rate</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {myWeeklyPerformance?.targetCompletionRate || 
                   (weeklyPerformance?.data?.length ? 
                     (weeklyPerformance.data.reduce((sum, user) => sum + parseFloat(user.targetCompletionRate), 0) / weeklyPerformance.data.length).toFixed(1) : 
                     '0')}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {weeklyPerformance?.totalUsers || 1}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Performance Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Weekly Performance</h2>
        
        {/* Week Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Week
          </label>
          <select
            value={selectedWeek}
            onChange={(e) => handleWeekChange(e.target.value)}
            className="w-full md:w-64 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {availableWeeks.map((week) => (
              <option key={week.weekId} value={week.weekId}>
                {week.weekName} ({week.startDate} - {week.endDate})
              </option>
            ))}
          </select>
        </div>

        {/* Performance Data */}
        {selectedWeek && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {isAdmin && weeklyPerformance ? (
              // Admin view - All users performance
              <div>
                <div className="px-6 py-4 bg-gray-50 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Week: {weeklyPerformance.weekRange.startDate} - {weeklyPerformance.weekRange.endDate}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {weeklyPerformance.totalUsers} users • {weeklyPerformance.weekRange.startDay} - {weeklyPerformance.weekRange.endDay}
                  </p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weekly Target
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Achieved
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Completion Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {weeklyPerformance.data.map((user) => (
                        <tr key={user.userId}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.userName}</div>
                              <div className="text-sm text-gray-500">{user.userEmail}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.weeklyTarget}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{user.weeklyAchieved}</div>
                            <div className="text-sm text-gray-500">
                              {user.validLeads} valid / {user.totalLeads} total
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-semibold ${getProgressColor({ percentage: user.targetCompletionRate })}`}>
                              {user.targetCompletionRate}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => openTargetModal(user.userId, format(new Date(), 'yyyy-MM-dd'))}
                              className="text-blue-600 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-3 py-1 rounded-md text-xs"
                            >
                              Set Target
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : !isAdmin && myWeeklyPerformance ? (
              // User view - Own performance
              <div>
                <div className="px-6 py-4 bg-gray-50 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">
                    My Performance: {myWeeklyPerformance.weekRange.startDate} - {myWeeklyPerformance.weekRange.endDate}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {myWeeklyPerformance.weekRange.startDay} - {myWeeklyPerformance.weekRange.endDay}
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{myWeeklyPerformance.weeklyTarget}</div>
                      <div className="text-sm text-gray-600">Weekly Target</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{myWeeklyPerformance.weeklyAchieved}</div>
                      <div className="text-sm text-gray-600">Achieved</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{myWeeklyPerformance.validLeads}</div>
                      <div className="text-sm text-gray-600">Valid Leads</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getProgressColor({ percentage: myWeeklyPerformance.targetCompletionRate })}`}>
                        {myWeeklyPerformance.targetCompletionRate}%
                      </div>
                      <div className="text-sm text-gray-600">Completion Rate</div>
                    </div>
                  </div>

                  {/* Daily Breakdown */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Day
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Target
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Achieved
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Valid/Total
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {myWeeklyPerformance.dailyBreakdown.map((day) => (
                          <tr key={day.date}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {day.dayName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {day.target}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {day.achieved}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {day.validLeads}/{day.totalLeads}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor({ isTargetMet: day.isTargetMet })}`}>
                                <span className="mr-1">{getStatusIcon({ isTargetMet: day.isTargetMet })}</span>
                                {day.isTargetMet ? 'Met' : 'Not Met'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No performance data available for the selected week.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Set Target Modal */}
      {targetModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Set Daily Target</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Daily Target <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={dailyTarget}
                    onChange={(e) => setDailyTarget(parseInt(e.target.value) || 0)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter daily target (1-100)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={targetNotes}
                    onChange={(e) => setTargetNotes(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Add notes about the target"
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {targetNotes.length}/500 characters
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeTargetModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetTarget}
                  disabled={!targetDate || dailyTarget <= 0 || dailyTarget > 100}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Set Target
                </button>
              </div>
              
              {(!targetDate || dailyTarget <= 0 || dailyTarget > 100) && (
                <div className="mt-3 text-sm text-red-600">
                  {!targetDate && <div>• Please select a target date</div>}
                  {dailyTarget <= 0 && <div>• Daily target must be greater than 0</div>}
                  {dailyTarget > 100 && <div>• Daily target cannot exceed 100</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default Goals;
